import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, UntypedFormArray, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { MeasureRecord } from 'src/shared/models/measure-record.model';
import { PageInterface } from '../planner/page/page.interface';
import { ShiftRecord } from 'src/shared/models/shift-record.model';
import { ShiftRecordStatus } from 'src/shared/models/shift-record-status';
import { VarianceReason } from 'src/shared/models/variance-reason.model';
import { VarianceReasonRecord } from 'src/shared/models/variance-reason-record.model';
import { WorkflowPage } from 'src/shared/models/workflow-page.model';
import { WorkflowUtilityService } from 'src/shared/services/workflow-utility.service';
import { WorkflowPageComponent } from '../base-classes/workflow-page/workflow-page.component';
import { JobTitleWithTargetDifference } from 'src/shared/models/job-title-with-target-difference.model';
import { ShiftRecordServiceInterface } from 'src/shared/services/shift-record-service.interface';


@Component({
    selector: 'smt-enter-reasons',
    templateUrl: './enter-reasons.component.html',
    styleUrls: ['./enter-reasons.component.scss']
})
export class EnterReasonsComponent extends WorkflowPageComponent implements OnInit, PageInterface {
    @Input() page: WorkflowPage;
    @Output() previousRequested = new EventEmitter();

    public loading: boolean;
    public shiftRecord: ShiftRecord;
    public jobTitlesWithVariance: JobTitleWithTargetDifference[];
    public reasons: VarianceReasonRecord[] = [];
    private varianceWarningMessages: string[] = [];

    public reasonsForm: UntypedFormGroup = new UntypedFormGroup({
        reasonId: new UntypedFormControl(''),
        reasonExplanation: new UntypedFormControl(''),
        reasonsListForm: new UntypedFormArray([])
    });

    public measureForm: UntypedFormGroup = new UntypedFormGroup({});
    public measureFormValues$;

    public formGroups:UntypedFormGroup[];

    private shiftRecordService: ShiftRecordServiceInterface;

    private readonly editingReasonIdControlName: string = "EditingReasonId";
    private readonly editingExplanationControlName: string = "EditingExplanation";

    constructor(
        private _utility: WorkflowUtilityService,
        private _router: Router) {
        super(_router);
    }

    ngOnInit() {        
        super.initialize(this.page, () => this.initialize());
    }

    initialize() {
        this.shiftRecordService = this._utility.getShiftRecordService(this.shiftRecord.costCenter);
        this.reasons = this.shiftRecord.varianceReasonRecords;

        this.reasons.forEach((reason) => {
            let formGroup = new UntypedFormGroup({});
            formGroup.addControl(this.editingReasonIdControlName, new UntypedFormControl(reason.varianceReasonId));
            formGroup.addControl(this.editingExplanationControlName, new UntypedFormControl(reason.explanation));
            this.getReasonFormList().push(formGroup);
        });        

        this.updateJobTitlesWithVariance();

        // Measures form
        this.shiftRecord.costCenter.measures.forEach(measure => {
            let record = this.shiftRecord.measureRecords.find(r => r.measureId == measure.id);
            let value = record ? record.amount : '';
            this.measureForm.addControl(this.getMeasureControlName(measure.id), new UntypedFormControl(value, Validators.required));
        });

        this.formGroups = [ this.measureForm, this.reasonsForm ];
        this.varianceWarningMessages = this._utility.getVarianceWarnings();
    }    

    updateJobTitlesWithVariance(){
        let titlesWithVariance = [];

        this.shiftRecord.staffingPlans.forEach(plan => {
            let amount = plan.amount;
            let recommended = plan.recommended;
            let jobTitle = this.shiftRecord.costCenter.jobTitles.find(jobTitle => jobTitle.id == plan.jobTitleId);

            if (jobTitle && recommended !== null && amount != recommended) {
                titlesWithVariance.push(jobTitle);
            }
        });

        this.jobTitlesWithVariance = titlesWithVariance;
    }

    getReasonLabel(reasonId){
        const reason = this.shiftRecord.costCenter.varianceReasons.find(reason => reason.id == reasonId);

        if (!reason) return '';

        return reason.reason;
    }

    getMeasureControlName(measureId){
        return 'measure' + measureId;
    }

    isFormValid(): boolean {
        return this.measureForm && this.measureForm.valid && this.isVarianceReasonValid();
    }

    isVarianceReasonValid(){
        // if no variance, don't worry about reasons for variance
        if (!this.jobTitlesWithVariance.length) {
            return true;
        }

        // otherwise, require at least 1
        return this.reasons.length >= 1 && !this.isVarianceReasonDuplicated;
    }

    get isVarianceReasonDuplicated(){
        let uniqueReasons = this.reasons.filter((value, index, self) => { 
            return self.findIndex(s => s.varianceReasonId === value.varianceReasonId) === index;
        });
        return this.reasons.length !== uniqueReasons.length;
    }

    showEditedName(){
        return !!this.shiftRecord.user;
    }

    getReasonFormList(){
        return this.reasonsForm.get('reasonsListForm') as UntypedFormArray;
    }

    get reasonOptions() {
        return this.shiftRecord.costCenter.varianceReasons.filter((reason: VarianceReason) => {
            return this.reasons.findIndex(r => r.varianceReasonId === reason.id) === -1;
        });
    }

    get isReasonNotSelected(){
        return !Number(this.reasonsForm.value['reasonId']);
    }

    addReason(){
        let newReason = new VarianceReasonRecord();
        let formGroup = new UntypedFormGroup({});
        let reasonId = Number(this.reasonsForm.value['reasonId']);
        let reasonExplanation = this.reasonsForm.value['reasonExplanation'];

        if (!reasonId) {
            return;
        }

        if (!reasonExplanation) {
            reasonExplanation = '';
        }

        newReason.varianceReasonId = +reasonId;
        newReason.explanation = reasonExplanation;

        formGroup.addControl(this.editingReasonIdControlName, new UntypedFormControl(newReason.varianceReasonId));
        formGroup.addControl(this.editingExplanationControlName, new UntypedFormControl(newReason.explanation));

        this.getReasonFormList().push(formGroup);

        this.reasons.push(newReason);

        this.cancelReason();
    }

    cancelReason(){
        this.reasonsForm.get('reasonId').reset();
        this.reasonsForm.get('reasonExplanation').reset();
    }

    editReason(index){
        this.reasons[index].editing = true;
    }

    applyReasonEdit(index){
        let reason = this.getReasonFormList().at(index);
        this.reasons[index].varianceReasonId = +reason.value[this.editingReasonIdControlName];
        this.reasons[index].explanation = reason.value[this.editingExplanationControlName];
        this.reasons[index].editing = false;
        
        //setValue will trigger 'valueChanges' which is necessary for formValidation
        reason.get(this.editingReasonIdControlName).setValue(this.reasons[index].varianceReasonId);
        reason.get(this.editingExplanationControlName).setValue(this.reasons[index].explanation);
    }

    cancelReasonEdit(index){
        let reason = this.getReasonFormList().at(index);
        reason.get(this.editingReasonIdControlName).reset(this.reasons[index].varianceReasonId);
        reason.get(this.editingExplanationControlName).reset(this.reasons[index].explanation);
        this.reasons[index].editing = false;
    }

    deleteReason(index){
        this.reasons.splice(index, 1);
        this.getReasonFormList().removeAt(index);
    }

    setReasonRecords(){
        this.reasons.forEach(reason => {
            reason.shiftBlockId = this.shiftRecord.shiftBlockId;
            reason.workflowTimeFacility = this.shiftRecord.workflowTimeFacility;
        });

        this.shiftRecord.varianceReasonRecords = this.reasons;
    }

    setMeasures(){
        let measureRecords = this.shiftRecord.measureRecords;
        this.shiftRecord.measureRecords = [];

        this.shiftRecord.costCenter.measures.forEach(measure => {
            let measureRecord = measureRecords.find(s => s.measureId == measure.id) || new MeasureRecord();

            measureRecord.workflowTimeFacility = this.shiftRecord.workflowTimeFacility;
            measureRecord.shiftBlockId = this.shiftRecord.shiftBlockId;
            measureRecord.measureId = measure.id;
            measureRecord.amount = this.measureForm.value[this.getMeasureControlName(measure.id)] || 0;

            this.shiftRecord.measureRecords.push(measureRecord);
        });
    }

    setStatus() {
        this.shiftRecord.status = ShiftRecordStatus.Done;
    }

    save(){
        this.setReasonRecords();
        this.setMeasures();
        this.setStatus();
        return this.shiftRecordService.setShiftRecordWithAssociated(this.shiftRecord);
    }

    shouldSkipSave(): boolean {
        return false;
    }
}
